import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
  XCircleIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/AdminLayout';

interface Customer {
  name: string;
  phone: string;
  state: string;
  city: string;
  address: string;
}

interface OrderItem {
  product: {
    _id: string;
    name: string;
    brand: string;
    images: string[];
  };
  size: string;
  quantity: number;
  price: number;
}

interface Order {
  _id: string;
  orderNumber: string;
  customer: Customer;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  whatsappSent: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, statusFilter]);

  const fetchOrders = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('No authentication token found. Please login again.');
        setLoading(false);
        return;
      }

      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const response = await fetch(`/api/orders?page=${currentPage}&limit=10${statusParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setOrders(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
      } else {
        setError(data.message || 'Failed to fetch orders');
      }
    } catch (err: any) {
      setError(`Failed to load orders: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        fetchOrders();
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus as any });
        }
        toast.success('Status updated');
      } else {
        toast.error('Failed to update status');
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'confirmed':
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      case 'shipped':
        return <TruckIcon className="h-5 w-5 text-purple-500" />;
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openWhatsApp = (order: Order) => {
    const message = `
مرحباً ${order.customer.name}،

طلبكم رقم ${order.orderNumber} تم استلامه بنجاح ✅

تفاصيل الطلب:
${order.items.map(item => 
  `• ${item.product?.brand || 'N/A'} - ${item.product?.name || 'Product deleted'}
  المقاس: ${item.size} | الكمية: ${item.quantity} | السعر: ${item.price.toLocaleString()} DA`
).join('\n')}

المجموع: ${order.total.toLocaleString()} DA

سيتم التواصل معكم قريباً لتأكيد التوصيل.

شكراً لثقتكم بنا! 🛍️
    `.trim();

    const cleanPhone = order.customer.phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <AdminLayout title="Orders">
        <div className="flex items-center justify-center py-32"><div className="spinner-lg" /></div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Orders">
        <div className="max-w-md mx-auto text-center py-16">
          <div className="bg-red-50 border border-red-200 p-4 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={() => { setError(null); setLoading(true); fetchOrders(); }}
            className="bg-offblack text-offwhite px-6 py-2 text-sm font-medium hover:bg-charcoal transition-colors"
          >
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Orders"
      subtitle={`${orders.length} order${orders.length !== 1 ? 's' : ''}${statusFilter !== 'all' ? ` · ${statusFilter}` : ''}`}
    >
      {/* Status Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-xs font-medium tracking-wide uppercase transition-colors ${
              statusFilter === status
                ? 'bg-offblack text-offwhite'
                : 'bg-white border border-warmgray-200 text-warmgray-600 hover:border-offblack hover:text-offblack'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-warmgray-200 overflow-hidden">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto text-warmgray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3 className="text-lg font-display text-offblack mb-1">No orders found</h3>
            <p className="text-sm text-warmgray-500">
              {statusFilter === 'all' ? 'Orders will appear here when customers place them.' : `No "${statusFilter}" orders found.`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-warmgray-200">
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Order</th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Customer</th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Items</th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Total</th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Status</th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Date</th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium tracking-wider uppercase text-warmgray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warmgray-100">
                {orders.map((order) => (
                  <tr
                    key={order._id}
                    className="hover:bg-warmgray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedOrder(order)}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-offblack">#{order.orderNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-offblack">{order.customer.name}</p>
                      <p className="text-xs text-warmgray-500">{order.customer.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-offblack">{order.items.length} item{order.items.length > 1 ? 's' : ''}</p>
                      <p className="text-xs text-warmgray-500 truncate max-w-[140px]">{order.items[0]?.product?.name || 'Product deleted'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-offblack">{order.total.toLocaleString()} DA</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {getStatusIcon(order.status)}
                        <span className={`text-[0.65rem] tracking-wide uppercase px-2 py-0.5 ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-warmgray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); openWhatsApp(order); }}
                        className="p-1.5 text-green-600 hover:bg-green-50 transition-colors"
                        title="Contact via WhatsApp"
                      >
                        <PhoneIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-warmgray-200 flex items-center justify-between">
            <span className="text-xs text-warmgray-500">Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                className="px-3 py-1 border border-warmgray-200 text-xs text-offblack hover:bg-warmgray-50 disabled:opacity-40 transition-colors">Previous</button>
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                className="px-3 py-1 border border-warmgray-200 text-xs text-offblack hover:bg-warmgray-50 disabled:opacity-40 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-warmgray-200">
                <div className="flex items-center gap-3">
                  <h3 className="font-display text-xl text-offblack">Order #{selectedOrder.orderNumber}</h3>
                  <span className={`text-[0.65rem] tracking-wide uppercase px-2 py-0.5 ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="p-1 hover:bg-warmgray-100 transition-colors">
                  <XMarkIcon className="w-5 h-5 text-warmgray-500" />
                </button>
              </div>

              {/* Customer */}
              <div className="bg-warmgray-50 p-4 mb-6">
                <h4 className="text-xs tracking-wider uppercase text-warmgray-500 mb-3">Customer</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-warmgray-500">Name</span><p className="font-medium text-offblack">{selectedOrder.customer.name}</p></div>
                  <div><span className="text-warmgray-500">Phone</span><p className="font-medium text-offblack">{selectedOrder.customer.phone}</p></div>
                  <div><span className="text-warmgray-500">State</span><p className="font-medium text-offblack">{selectedOrder.customer.state}</p></div>
                  <div><span className="text-warmgray-500">City</span><p className="font-medium text-offblack">{selectedOrder.customer.city}</p></div>
                  <div className="col-span-2"><span className="text-warmgray-500">Address</span><p className="font-medium text-offblack">{selectedOrder.customer.address}</p></div>
                </div>
              </div>

              {/* Items */}
              <div className="mb-6">
                <h4 className="text-xs tracking-wider uppercase text-warmgray-500 mb-3">Items</h4>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 border border-warmgray-200">
                      <img src={item.product?.images?.[0] || '/api/placeholder/64/64'} alt={item.product?.name || 'Product'}
                        className="w-14 h-14 object-contain bg-warmgray-100" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-offblack">{item.product?.name || 'Product deleted'}</p>
                        <p className="text-xs text-warmgray-500">Size: {item.size} · Qty: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-offblack">{item.price.toLocaleString()} DA</p>
                        <p className="text-xs text-warmgray-500">{(item.price * item.quantity).toLocaleString()} DA total</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-warmgray-50 p-4 mb-6">
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-warmgray-500">Subtotal</span><span className="text-offblack">{selectedOrder.subtotal.toLocaleString()} DA</span></div>
                  <div className="flex justify-between"><span className="text-warmgray-500">Delivery</span><span className="text-offblack">{selectedOrder.deliveryFee.toLocaleString()} DA</span></div>
                  <div className="flex justify-between font-medium text-lg pt-2 border-t border-warmgray-200">
                    <span className="text-offblack">Total</span><span className="text-offblack">{selectedOrder.total.toLocaleString()} DA</span>
                  </div>
                </div>
              </div>

              {/* Status Update */}
              <div className="mb-6">
                <h4 className="text-xs tracking-wider uppercase text-warmgray-500 mb-3">Update Status</h4>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map((status) => (
                    <button key={status} onClick={() => updateOrderStatus(selectedOrder._id, status)}
                      className={`px-3 py-1.5 text-xs font-medium tracking-wide uppercase transition-colors ${
                        selectedOrder.status === status
                          ? 'bg-offblack text-offwhite'
                          : 'bg-warmgray-100 text-warmgray-600 hover:bg-warmgray-200'
                      }`}>
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="mb-6">
                  <h4 className="text-xs tracking-wider uppercase text-warmgray-500 mb-2">Notes</h4>
                  <p className="text-sm text-warmgray-600 bg-warmgray-50 p-3">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => openWhatsApp(selectedOrder)}
                  className="flex-1 bg-green-600 text-white py-2.5 text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                  <PhoneIcon className="w-4 h-4" /> Contact Customer
                </button>
                <button onClick={() => setSelectedOrder(null)}
                  className="flex-1 border border-warmgray-200 py-2.5 text-sm font-medium text-offblack hover:bg-warmgray-50 transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
